import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { fetchDiscoverSalon, fetchSalonReviews } from '../../api/discover';
import { fetchMyReviews, submitReview } from '../../api/reviews';
import PublicReviewCard from '../../components/reviews/PublicReviewCard';
import ReviewModal from '../../components/reviews/ReviewModal';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import BookNowLink from '../../components/booking/BookNowLink';
import BookmarkButton from '../../components/explore/BookmarkButton';
import ExploreLayout from '../../components/explore/ExploreLayout';
import GalleryLightbox from '../../components/explore/GalleryLightbox';
import AudienceBadge from '../../components/salon/AudienceBadge';
import { useCart } from '../../context/CartContext';
import { CATEGORIES } from '../../constants/serviceCategories';
import { serviceAvailableAtBranch } from '../../utils/serviceBranches';

const TABS = ['services', 'products', 'gallery', 'reviews'];

const SalonProfilePage = () => {
  const { t } = useTranslation();
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { role } = useAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('services');
  const [salonReviewOpen, setSalonReviewOpen] = useState(false);
  const [branchId, setBranchId] = useState('');
  const [serviceCategory, setServiceCategory] = useState('');
  const [reviewPage, setReviewPage] = useState(1);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['discover-salon', tenantId],
    queryFn: () => fetchDiscoverSalon(tenantId),
  });

  const { data: reviewsData } = useQuery({
    queryKey: ['salon-reviews', tenantId, reviewPage],
    queryFn: () => fetchSalonReviews(tenantId, { page: reviewPage, limit: 10 }),
    enabled: tab === 'reviews',
  });

  const { data: myReviewsData } = useQuery({
    queryKey: ['my-reviews', 'salon-check', tenantId],
    queryFn: () => fetchMyReviews({ limit: 100 }),
    enabled: role === 'CLIENT',
  });

  const salonReviewMut = useMutation({
    mutationFn: submitReview,
    onSuccess: () => {
      showToast(t('toast.reviewSubmitted'), 'success');
      setSalonReviewOpen(false);
      queryClient.invalidateQueries({ queryKey: ['salon-reviews', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['my-reviews'] });
      queryClient.invalidateQueries({ queryKey: ['discover-salon', tenantId] });
    },
    onError: (err) => showToast(err.response?.data?.message || t('toast.errorGeneric'), 'error'),
  });

  const existingSalonReview = useMemo(
    () =>
      (myReviewsData?.data || []).find(
        (r) => r.type === 'SALON' && String(r.tenantId) === String(tenantId)
      ),
    [myReviewsData, tenantId]
  );

  const salon = data?.salon;
  const branches = data?.branches || [];
  const services = (data?.services || []).filter((s) => {
    if (serviceCategory && s.category !== serviceCategory) return false;
    if (!branchId) return true;
    return serviceAvailableAtBranch(s, branchId);
  });
  const products = data?.products || [];
  const gallery = data?.gallery || [];
  const reviews = reviewsData?.data || [];
  const summary = reviewsData?.summary || salon?.ratingSummary;

  if (isLoading) {
    return (
      <ExploreLayout>
        <p className="p-8 text-zinc-600 dark:text-zinc-500">{t('common.loading')}</p>
      </ExploreLayout>
    );
  }

  if (!salon) {
    return (
      <ExploreLayout>
        <p className="p-8">{t('explore.salonNotFound')}</p>
      </ExploreLayout>
    );
  }

  const defaultBranchId = branchId || branches[0]?._id || '';

  return (
    <ExploreLayout>
      <div className="relative h-56 md:h-72">
        {salon.coverImage ? (
          <img src={salon.coverImage} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="h-full bg-zinc-100 dark:bg-zinc-800" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-100 dark:from-zinc-950 via-zinc-200/40 dark:via-zinc-950/40 to-transparent" />
        <div className="absolute bottom-6 left-6 right-6">
          <Link to="/explore" className="text-sm text-glow-300 hover:underline">← {t('common.explore')}</Link>
          <div className="mt-2 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-3xl font-bold">{salon.businessName}</h1>
              <div className="mt-2">
                <AudienceBadge audienceType={salon.audienceType} />
              </div>
            </div>
            <BookmarkButton tenantId={tenantId} />
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-6 py-4">
        <div className="flex flex-wrap gap-4 text-sm text-zinc-600 dark:text-zinc-400">
          <span className="text-amber-400">
            ★ {salon.averageRating} ({t('explore.reviewsCount', { count: salon.totalReviews })})
          </span>
          <span>{salon.address}</span>
          {salon.phone && <span>{salon.phone}</span>}
        </div>

        <div className="mt-6 flex gap-2 overflow-x-auto border-b border-zinc-200 dark:border-zinc-800">
          {TABS.map((tabKey) => (
            <button
              key={tabKey}
              type="button"
              onClick={() => setTab(tabKey)}
              className={`shrink-0 border-b-2 px-4 py-2 text-sm capitalize ${tab === tabKey ? 'border-glow-500 text-glow-300' : 'border-transparent text-zinc-600 dark:text-zinc-500'}`}
            >
              {t(`explore.tabs.${tabKey}`)}
            </button>
          ))}
        </div>

        {tab === 'services' && (
          <div className="mt-6">
            {branches.length > 1 && (
              <select
                value={branchId}
                onChange={(e) => setBranchId(e.target.value)}
                className="mb-4 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-3 py-2 text-sm"
              >
                <option value="">{t('explore.allBranches')}</option>
                {branches.map((b) => (
                  <option key={b._id} value={b._id}>{b.name}</option>
                ))}
              </select>
            )}
            <div className="mb-4 flex flex-wrap gap-2">
              <button type="button" onClick={() => setServiceCategory('')} className={`rounded-full px-3 py-1 text-xs ${!serviceCategory ? 'bg-glow-600/30 text-glow-300' : 'bg-zinc-100 dark:bg-zinc-800'}`}>
                {t('common.all')}
              </button>
              {CATEGORIES.map((c) => (
                <button key={c} type="button" onClick={() => setServiceCategory(c)} className={`rounded-full px-3 py-1 text-xs ${serviceCategory === c ? 'bg-glow-600/30 text-glow-300' : 'bg-zinc-100 dark:bg-zinc-800'}`}>
                  {c}
                </button>
              ))}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {services.map((s) => (
                <div key={s._id} className="flex gap-4 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
                  {s.imageUrl && <img src={s.imageUrl} alt="" className="h-20 w-20 rounded-lg object-cover" />}
                  <div className="flex-1">
                    <p className="font-medium">{s.name}</p>
                    <p className="text-xs text-zinc-600 dark:text-zinc-500">{t('common.duration', { minutes: s.duration })} · {s.category}</p>
                    <p className="mt-1 text-glow-300">{t('common.currency', { amount: s.price?.toLocaleString() })}</p>
                    <BookNowLink
                      tenantId={tenantId}
                      branchId={defaultBranchId}
                      serviceId={s._id}
                      className="mt-2 inline-block rounded-lg bg-glow-600 px-3 py-1.5 text-xs text-white"
                    >
                      {t('common.bookNow')}
                    </BookNowLink>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === 'products' && (
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {products.map((p) => (
              <div key={p._id} className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
                {p.images?.[0]?.url && <img src={p.images[0].url} alt="" className="mb-3 h-32 w-full rounded-lg object-cover" />}
                <p className="font-medium">{p.name}</p>
                <p className="text-glow-300">{t('common.currency', { amount: p.price?.toLocaleString() })}</p>
                <p className="text-xs text-zinc-600 dark:text-zinc-500">
                  {p.stock > 0 ? t('explore.inStockCount', { count: p.stock }) : t('marketplace.outOfStock')}
                </p>
                <button
                  type="button"
                  disabled={p.stock <= 0}
                  onClick={() => {
                    addItem({ ...p, tenantId, salonName: salon.businessName }, 1);
                    navigate('/client/cart');
                  }}
                  className="mt-3 w-full rounded-lg border border-zinc-300 dark:border-zinc-700 py-1.5 text-xs disabled:opacity-40"
                >
                  {t('explore.order')}
                </button>
              </div>
            ))}
          </div>
        )}

        {tab === 'gallery' && (
          <div className="mt-6 columns-2 gap-3 md:columns-3">
            {gallery.map((img, i) => (
              <button key={img._id} type="button" onClick={() => setLightboxIndex(i)} className="mb-3 block w-full">
                <img src={img.imageUrl} alt="" className="w-full rounded-lg object-cover" />
              </button>
            ))}
          </div>
        )}

        {tab === 'reviews' && (
          <div className="mt-6">
            {role === 'CLIENT' && !existingSalonReview && (
              <button
                type="button"
                onClick={() => setSalonReviewOpen(true)}
                className="mb-6 rounded-lg border border-amber-700/50 px-4 py-2 text-sm text-amber-300"
              >
                {t('reviews.reviewSalon')}
              </button>
            )}
            {existingSalonReview && (
              <p className="mb-4 text-sm text-zinc-500">{t('reviews.alreadyReviewedSalon')}</p>
            )}
            {summary && (
              <div className="mb-8 flex flex-wrap gap-8 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-6">
                <div className="text-center">
                  <p className="text-4xl font-bold text-amber-400">{summary.averageRating}</p>
                  <p className="text-sm text-zinc-600 dark:text-zinc-500">{t('explore.reviewsCount', { count: summary.totalReviews })}</p>
                </div>
                <div className="flex-1 space-y-2">
                  {summary.breakdown?.map((row) => (
                    <div key={row.star} className="flex items-center gap-2 text-xs">
                      <span className="w-8">{row.star}★</span>
                      <div className="h-2 flex-1 overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
                        <div className="h-full bg-amber-400" style={{ width: `${row.percent}%` }} />
                      </div>
                      <span className="w-10 text-zinc-600 dark:text-zinc-500">{row.percent}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="space-y-4">
              {reviews.map((r) => (
                <PublicReviewCard key={r._id} review={r} />
              ))}
            </div>
            {reviewsData?.pagination?.pages > 1 && (
              <div className="mt-6 flex justify-center gap-4">
                <button type="button" disabled={reviewPage <= 1} onClick={() => setReviewPage((p) => p - 1)} className="text-sm text-glow-400 disabled:opacity-40">
                  {t('common.prev')}
                </button>
                <button type="button" disabled={reviewPage >= reviewsData.pagination.pages} onClick={() => setReviewPage((p) => p + 1)} className="text-sm text-glow-400 disabled:opacity-40">
                  {t('common.next')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="sticky bottom-0 border-t border-zinc-200 dark:border-zinc-800 bg-zinc-50/95 dark:bg-zinc-950/95 px-6 py-4 backdrop-blur">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-medium">{salon.businessName}</p>
            <p className="text-xs text-amber-400">★ {salon.averageRating}</p>
          </div>
          <div className="flex gap-2">
            <BookNowLink
              tenantId={tenantId}
              branchId={defaultBranchId}
              className="rounded-lg bg-glow-600 px-4 py-2 text-sm text-white"
            >
              📅 {t('explore.bookAppointment')}
            </BookNowLink>
            <button
              type="button"
              onClick={() => setTab('products')}
              className="rounded-lg border border-zinc-300 dark:border-zinc-700 px-4 py-2 text-sm"
            >
              🛍 {t('explore.browseProducts')}
            </button>
          </div>
        </div>
      </div>

      <ReviewModal
        open={salonReviewOpen}
        title={t('reviews.reviewSalon')}
        subtitle={t('reviews.salonExperience', { salon: salon.businessName })}
        onClose={() => setSalonReviewOpen(false)}
        saving={salonReviewMut.isPending}
        onSubmit={({ rating, comment }) =>
          salonReviewMut.mutate({ type: 'SALON', tenantId, rating, comment })
        }
      />

      <GalleryLightbox
        images={gallery}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onNavigate={(dir) => {
          setLightboxIndex((i) => {
            const next = (i + dir + gallery.length) % gallery.length;
            return next;
          });
        }}
      />
    </ExploreLayout>
  );
};

export default SalonProfilePage;
